(() => {
  'use strict';

  const UPSELL_SELECTOR = '.csh-upsell-cart-container';
  const UPSELL_SOURCE_PROPERTY = '_twisty_upsell_source';
  const DRAWER_UPSELL_TAG = 'twisty-upsell-cartdrawer-1';
  const CART_UPSELL_TAG = 'twisty-upsell-cart-1';

  const currentPath = window.location.pathname.replace(/\/$/, '');
  const isCartPage = currentPath === '/cart';

  let latestCartItemCount = null;
  let scheduled = false;

  function isCartUpsell(upsell) {
    return isCartPage ||
      upsell.classList.contains('csh-upsell-cart-container--cart-page') ||
      Boolean(upsell.closest('cart-drawer, .cart-drawer, .cart-drawer__form'));
  }

  function shouldShowUpsell(upsell) {
    if (isCartUpsell(upsell)) {
      // Liquid's cart markers describe the initial page load. After an AJAX
      // removal, the live cart response is authoritative; never leave a
      // preserved Customily recommendation mounted in an empty cart.
      if (latestCartItemCount === 0) return false;

    }

    return true;
  }

  function updateUpsells() {
    scheduled = false;

    document.querySelectorAll(UPSELL_SELECTOR).forEach(upsell => {
      // Wrap the vendor builder as soon as the widget mounts, so Customily's
      // own cart-page add path is covered even before any of our handlers run.
      customilyUpsellComponent(upsell);
      if (isCartPage && !upsell.closest('cart-drawer, .cart-drawer')) {
        const cartForm = document.querySelector('form#cart.cart__contents');
        const cartSummary = cartForm?.querySelector('.cart__summary');
        const productRecommendations = document.querySelector(
          'form#cart product-recommendations.cart__recommendations, product-recommendations.cart__recommendations'
        );
        // Customily mounts its cart-page upsell asynchronously and may append
        // it beside/below the cart item list. Keep it immediately after the
        // theme's product-recommendations block wherever that async component
        // is actually rendered in the cart layout.
        if (productRecommendations) {
          if (upsell.previousElementSibling !== productRecommendations) {
            productRecommendations.insertAdjacentElement('afterend', upsell);
          }
        } else if (cartSummary && upsell.parentElement !== cartSummary) {
          cartSummary.appendChild(upsell);
        }
      }
      const show = shouldShowUpsell(upsell);
      if (show) {
        if (upsell.dataset.gsBlockedImageMethodHidden === 'true') {
          upsell.style.removeProperty('display');
          upsell.hidden = false;
          delete upsell.dataset.gsBlockedImageMethodHidden;
        }
        return;
      }

      upsell.hidden = true;
      upsell.dataset.gsBlockedImageMethodHidden = 'true';
      upsell.style.setProperty('display', 'none', 'important');
    });
  }

  function queueUpdate() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(updateUpsells);
  }

  function handleCartSectionsRendered() {
    // Cart-add responses replace the drawer HTML but do not emit the cart
    // state event used by quantity/removal requests. A rendered line item is
    // enough to invalidate a previously remembered empty-cart state.
    if (document.querySelector('cart-drawer .cart-item, .cart-drawer .cart-item, #main-cart-items .cart-item')) {
      latestCartItemCount = null;
    }
    queueUpdate();
  }

  function customizationIdFromProperties(properties) {
    const customizationUrl = properties?.['_customily-customization-url'];
    if (!customizationUrl) return '';
    try {
      return new URL(String(customizationUrl), window.location.origin).searchParams.get('customizationId') || '';
    } catch (_) {
      return '';
    }
  }

  // Customily builds upsell line items with preview URLs on its S3 bucket,
  // in either path style (s3.<region>.amazonaws.com/customily/<key>, what its
  // preview generator emits today) or virtual-host style
  // (customily.s3.<region>.amazonaws.com/<key>). The checkout design-preview
  // extension only trusts *.customily.com and Shopify CDN hosts, so those
  // lines lose their "Preview Your Design" link. The same key is served from
  // cdn.customily.com/<key>; swap to the CDN once that copy is confirmed to
  // load, otherwise keep the original URL untouched.
  const PREVIEW_PROPERTY_KEYS = ['_customily-preview', '_customily-thumb'];
  const CUSTOMILY_S3_BUCKET = 'customily';
  const CUSTOMILY_S3_VHOST_RE = /^customily\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
  const S3_PATH_STYLE_HOST_RE = /^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
  const CUSTOMILY_CDN_HOST = 'cdn.customily.com';
  const PREVIEW_CHECK_TIMEOUT_MS = 4000;

  function customilyCdnPreviewUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    let url;
    try {
      url = new URL(value.trim());
    } catch (_) {
      return null;
    }
    if (url.protocol !== 'https:') return null;
    let key = null;
    if (CUSTOMILY_S3_VHOST_RE.test(url.hostname)) {
      key = url.pathname;
    } else if (S3_PATH_STYLE_HOST_RE.test(url.hostname)) {
      const bucketPrefix = `/${CUSTOMILY_S3_BUCKET}/`;
      if (!url.pathname.startsWith(bucketPrefix)) return null;
      key = url.pathname.slice(bucketPrefix.length - 1);
    }
    if (!key || key === '/') return null;
    return `https://${CUSTOMILY_CDN_HOST}${key}${url.search}`;
  }

  function imageLoads(url, timeoutMs = PREVIEW_CHECK_TIMEOUT_MS) {
    return new Promise(resolve => {
      let settled = false;
      const finish = ok => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(ok);
      };
      const timer = setTimeout(() => finish(false), timeoutMs);
      const image = new Image();
      image.onload = () => finish(true);
      image.onerror = () => finish(false);
      image.src = url;
    });
  }

  async function normalizeUpsellPreviewProperties(properties, check = imageLoads) {
    if (!properties || typeof properties !== 'object') return properties;
    const next = { ...properties };
    const verified = new Map();
    for (const key of PREVIEW_PROPERTY_KEYS) {
      const cdnUrl = customilyCdnPreviewUrl(next[key]);
      if (!cdnUrl) continue;
      if (!verified.has(cdnUrl)) {
        let ok = false;
        try { ok = await check(cdnUrl); } catch (_) { ok = false; }
        verified.set(cdnUrl, ok);
      }
      if (verified.get(cdnUrl)) next[key] = cdnUrl;
    }
    return next;
  }

  function wrapBuildProduct(component) {
    if (!component || typeof component.buildProduct !== 'function') return;
    if (component.buildProduct.gsPreviewNormalized) return;
    const original = component.buildProduct;
    const wrapped = async function (...args) {
      const cartItem = await original.apply(this, args);
      if (cartItem && typeof cartItem === 'object' && cartItem.properties) {
        cartItem.properties = await normalizeUpsellPreviewProperties(cartItem.properties);
      }
      return cartItem;
    };
    wrapped.gsPreviewNormalized = true;
    component.buildProduct = wrapped;
  }

  function customilyUpsellComponent(upsell) {
    const pending = [upsell?.__vue__].filter(Boolean);
    const visited = new Set();
    while (pending.length) {
      const component = pending.shift();
      if (!component || visited.has(component)) continue;
      visited.add(component);
      if (typeof component.updateImagePreview === 'function') {
        // Wrap here so both the drawer path below and Customily's own
        // cart-page click handler produce CDN-hosted preview properties.
        wrapBuildProduct(component);
        return component;
      }
      pending.push(...(component.$children || []));
    }
    return null;
  }

  function refreshPersonalizedUpsells(event) {
    const properties = event.detail?.properties;
    const customizationId = customizationIdFromProperties(properties);
    if (!properties || !customizationId) return;

    document.querySelectorAll(UPSELL_SELECTOR).forEach(upsell => {
      const component = customilyUpsellComponent(upsell);
      if (!component) return;

      // Customily's Vue upsell captures these values only when it is created.
      // An Edit Personalization transaction changes both without recreating
      // the component, so rebind them before asking Customily to regenerate
      // the recommendation previews.
      component.customizationId = customizationId;
      component.properties = { ...properties };
      (component.products || []).forEach(product => { product.loading = true; });
      component.$forceUpdate?.();
      Promise.resolve(component.updateImagePreview()).catch(error => {
        console.error('[GolfSwag] Could not refresh Customily cart upsell.', error);
      });
    });

    queueUpdate();
  }

  function markUpsellSource(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(`${UPSELL_SELECTOR} .csh-product-btn:not(.csh-product-btn-added)`);
    const upsell = button?.closest(UPSELL_SELECTOR);
    if (!button || !upsell) return;

    const component = customilyUpsellComponent(upsell);
    if (!component) return;

    // Customily clones the component properties when it builds the upsell line.
    // Stamp the source during capture phase, before its Vue click handler runs.
    component.properties = {
      ...(component.properties || {}),
      [UPSELL_SOURCE_PROPERTY]: upsell.closest('cart-drawer, .cart-drawer') ? DRAWER_UPSELL_TAG : CART_UPSELL_TAG
    };
  }

  async function addUpsellProductToDrawer(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(`${UPSELL_SELECTOR} .csh-product-btn:not(.csh-product-btn-added)`);
    const upsell = button?.closest(UPSELL_SELECTOR);
    if (!button || !upsell?.closest('cart-drawer, .cart-drawer')) return;

    // Claim drawer clicks before readiness checks, so Customily cannot fall
    // back to its redirecting handler or submit a duplicate pending add.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.dataset.gsCartAddPending === 'true') return;

    const component = customilyUpsellComponent(upsell);
    const card = button.closest('.csh-product-card');
    const productIndex = Array.from(upsell.querySelectorAll('.csh-product-card')).indexOf(card);
    const product = component?.products?.[productIndex];
    const drawer = upsell.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (!component || !product || typeof component.buildProduct !== 'function' || !drawer?.renderContents) {
      let errorMessage = upsell.querySelector('[data-gs-upsell-add-error]');
      if (!errorMessage) {
        errorMessage = document.createElement('p');
        errorMessage.setAttribute('data-gs-upsell-add-error', '');
        errorMessage.setAttribute('role', 'alert');
        upsell.appendChild(errorMessage);
      }
      errorMessage.textContent = 'This product is still loading. Please try again in a moment.';
      return;
    }
    upsell.querySelector('[data-gs-upsell-add-error]')?.remove();

    // Customily's own handler redirects to /cart after a successful upsell
    // add. Own only drawer-originated clicks and use the theme's AJAX section
    // response so the shopper remains in the open drawer.

    const originalButtonText = button.textContent;
    button.textContent = 'Adding…';
    button.dataset.gsCartAddPending = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const cartItem = await component.buildProduct(product);
      cartItem.properties = {
        ...(cartItem.properties || {}),
        [UPSELL_SOURCE_PROPERTY]: DRAWER_UPSELL_TAG
      };
      const sections = drawer.getSectionsToRender().map(section => section.section);
      const response = await fetch(`${routes.cart_add_url}?customily_upsell_atc=1`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          ...cartItem,
          sections,
          sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || 'Could not add the recommended product.');
      }

      button.disabled = false;
      button.removeAttribute('aria-busy');
      delete button.dataset.gsCartAddPending;
      product.added = true;
      button.textContent = 'Added';
      component.$forceUpdate?.();
      drawer.setActiveElement?.(button);
      drawer.renderContents(result, true);
    } catch (error) {
      console.error('[GolfSwag] Could not add Customily upsell to cart drawer.', error);
      button.textContent = product.added ? 'Added' : originalButtonText;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      delete button.dataset.gsCartAddPending;
    }
  }

  function syncUpsellAddedState(event) {
    const items = event.detail?.items;
    if (!Array.isArray(items)) return;

    latestCartItemCount = items.reduce((total, item) => total + Number(item.quantity ?? 1), 0);

    const cartVariantIds = new Set(items.map(item => String(item.variant_id ?? item.variantId ?? '')));
    document.querySelectorAll(UPSELL_SELECTOR).forEach(upsell => {
      const component = customilyUpsellComponent(upsell);
      if (!component) return;

      let changed = false;
      const cards = Array.from(upsell.querySelectorAll('.csh-product-card'));
      (component.products || []).forEach((product, index) => {
        const isInCart = cartVariantIds.has(String(product.variantId ?? ''));
        if (!isInCart) {
          const button = cards[index]?.querySelector('.csh-product-btn');
          if (button) {
            button.textContent = 'Add to cart';
            button.classList.remove('csh-product-btn-added');
            button.disabled = false;
            button.removeAttribute('aria-busy');
            delete button.dataset.gsCartAddPending;
          }
        }
        if (product.added === isInCart) return;
        product.added = isInCart;
        changed = true;
      });
      if (changed) component.$forceUpdate?.();
    });
    queueUpdate();
  }

  function captureCartAdd(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addToCartTrigger = target.closest(
      'button[name="add"], [data-add-to-cart], .product-form__submit, .add-to-cart'
    );
    if (!addToCartTrigger) return;

    latestCartItemCount = null;
    queueUpdate();
  }

  document.addEventListener('click', captureCartAdd, true);
  document.addEventListener('click', markUpsellSource, true);
  document.addEventListener('click', addUpsellProductToDrawer, true);
  document.addEventListener('submit', event => {
    const form = event.target instanceof Element ? event.target : null;
    if (!form?.matches('form[action*="/cart/add"], product-form form')) return;
    latestCartItemCount = null;
    queueUpdate();
  }, true);

  window.addEventListener('customily-options-loaded', queueUpdate);
  document.addEventListener('gs:cart-sections-rendered', handleCartSectionsRendered);
  document.addEventListener('gs:customily-cart-upsell-refresh', refreshPersonalizedUpsells);
  document.addEventListener('gs:cart-state-rendered', syncUpsellAddedState);

  const observer = new MutationObserver(queueUpdate);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'src', 'srcset', 'sizes']
  });
  queueUpdate();
})();
